import { useState, useEffect, useRef } from 'react';
import type { User } from '@/types';
import { sectors } from '@/mocks/formData';
import { formatCpf, isValidCpf, onlyCpfDigits } from '@/lib/cpf';

interface UserModalProps {
  isOpen: boolean;
  editingUser: User | null;
  onClose: () => void;
  onSave: (data: FormData) => void;
}

interface FormData {
  name: string;
  email: string;
  cpf: string;
  password: string;
  setor: string;
  perfil: 'admin' | 'colaborador';
  status: 'ativo' | 'inativo';
  forcePasswordReset: boolean;
}

const initialForm: FormData = {
  name: '',
  email: '',
  cpf: '',
  password: '',
  setor: 'Produção',
  perfil: 'colaborador',
  status: 'ativo',
  forcePasswordReset: true,
};

export default function UserModal({ isOpen, editingUser, onClose, onSave }: UserModalProps) {
  const [form, setForm] = useState<FormData>(initialForm);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (editingUser) {
      setForm({
        name: editingUser.name,
        email: editingUser.email,
        cpf: formatCpf(editingUser.cpf),
        password: '',
        setor: editingUser.setor,
        perfil: editingUser.perfil,
        status: editingUser.status,
        forcePasswordReset: false,
      });
    } else {
      setForm(initialForm);
    }
    setErrors({});
  }, [editingUser, isOpen]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};
    if (!form.name.trim()) newErrors.name = 'Nome é obrigatório.';
    if (!form.email.trim()) {
      newErrors.email = 'E-mail é obrigatório.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = 'E-mail inválido.';
    }
    if (!form.cpf.trim()) {
      newErrors.cpf = 'CPF é obrigatório.';
    } else if (!isValidCpf(form.cpf)) {
      newErrors.cpf = 'CPF inválido.';
    }
    if (!editingUser && !form.password.trim()) {
      newErrors.password = 'Senha é obrigatória para novo usuário.';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    onSave({ ...form, cpf: onlyCpfDigits(form.cpf) });
    onClose();
  };

  const updateField = (field: keyof FormData, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm"
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
    >
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-lg animate-[fadeIn_0.2s_ease-out]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-portal-gray-100">
          <h3 className="text-base font-semibold text-portal-gray-800">
            {editingUser ?'Editar Usuário' : 'Novo Usuário'}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-portal-gray-400 hover:text-portal-gray-600 hover:bg-portal-gray-100 transition-colors cursor-pointer"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-portal-gray-600 mb-1.5">
              Nome completo <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => updateField('name', e.target.value)}
              className={`w-full text-sm px-3 py-2.5 rounded-lg border ${
                errors.name ?'border-red-300 focus:ring-red-200' : 'border-portal-gray-200 focus:ring-portal-purple/20'
              } focus:outline-none focus:ring-2 focus:border-portal-purple bg-white text-portal-gray-800`}
              placeholder="Nome do colaborador"
            />
            {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-xs font-medium text-portal-gray-600 mb-1.5">
              E-mail <span className="text-red-400">*</span>
            </label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => updateField('email', e.target.value)}
              className={`w-full text-sm px-3 py-2.5 rounded-lg border ${
                errors.email ?'border-red-300 focus:ring-red-200' : 'border-portal-gray-200 focus:ring-portal-purple/20'
              } focus:outline-none focus:ring-2 focus:border-portal-purple bg-white text-portal-gray-800`}
              placeholder="email@empresa.com"
            />
            {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
          </div>

          <div>
            <label className="block text-xs font-medium text-portal-gray-600 mb-1.5">
              CPF <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={form.cpf}
              onChange={(e) => updateField('cpf', formatCpf(e.target.value))}
              className={`w-full text-sm px-3 py-2.5 rounded-lg border ${
                errors.cpf ?'border-red-300 focus:ring-red-200' : 'border-portal-gray-200 focus:ring-portal-purple/20'
              } focus:outline-none focus:ring-2 focus:border-portal-purple bg-white text-portal-gray-800`}
              placeholder="000.000.000-00"
            />
            {errors.cpf && <p className="text-xs text-red-500 mt-1">{errors.cpf}</p>}
          </div>

          <div>
            <label className="block text-xs font-medium text-portal-gray-600 mb-1.5">
              {editingUser ?'Nova senha (deixe em branco para manter)' : 'Senha'} {!editingUser && <span className="text-red-400">*</span>}
            </label>
            <input
              type="password"
              value={form.password}
              onChange={(e) => updateField('password', e.target.value)}
              className={`w-full text-sm px-3 py-2.5 rounded-lg border ${
                errors.password ?'border-red-300 focus:ring-red-200' : 'border-portal-gray-200 focus:ring-portal-purple/20'
              } focus:outline-none focus:ring-2 focus:border-portal-purple bg-white text-portal-gray-800`}
              placeholder={editingUser ? 'Opcional' : 'Senha inicial'}
            />
            {errors.password && <p className="text-xs text-red-500 mt-1">{errors.password}</p>}
            {editingUser && form.password.trim().length > 0 && (
              <label className="mt-3 flex items-start gap-2 rounded-lg bg-portal-gray-50 px-3 py-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.forcePasswordReset}
                  onChange={(e) => updateField('forcePasswordReset', e.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-portal-purple"
                />
                <span className="text-xs text-portal-gray-600">
                  Forçar reset de senha no próximo acesso
                </span>
              </label>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-portal-gray-600 mb-1.5">Setor</label>
              <select
                value={form.setor}
                onChange={(e) => updateField('setor', e.target.value)}
                className="w-full text-sm px-3 py-2.5 rounded-lg border border-portal-gray-200 focus:outline-none focus:ring-2 focus:ring-portal-purple/20 focus:border-portal-purple bg-white text-portal-gray-800 cursor-pointer"
              >
                {sectors.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-portal-gray-600 mb-1.5">Perfil</label>
              <select
                value={form.perfil}
                onChange={(e) => updateField('perfil', e.target.value as 'admin' | 'colaborador')}
                className="w-full text-sm px-3 py-2.5 rounded-lg border border-portal-gray-200 focus:outline-none focus:ring-2 focus:ring-portal-purple/20 focus:border-portal-purple bg-white text-portal-gray-800 cursor-pointer"
              >
                <option value="colaborador">Colaborador</option>
                <option value="admin">Administrador</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-portal-gray-600 mb-1.5">Status</label>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="status"
                  value="ativo"
                  checked={form.status === 'ativo'}
                  onChange={(e) => updateField('status', e.target.value)}
                  className="w-4 h-4 text-portal-purple focus:ring-portal-purple/20"
                />
                <span className="text-sm text-portal-gray-700">Ativo</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="status"
                  value="inativo"
                  checked={form.status === 'inativo'}
                  onChange={(e) => updateField('status', e.target.value)}
                  className="w-4 h-4 text-portal-purple focus:ring-portal-purple/20"
                />
                <span className="text-sm text-portal-gray-700">Inativo</span>
              </label>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-3 border-t border-portal-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-portal-gray-600 bg-white border border-portal-gray-200 rounded-lg hover:bg-portal-gray-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-5 py-2 text-sm font-medium text-white bg-portal-purple rounded-lg hover:bg-portal-purple-dark transition-colors cursor-pointer whitespace-nowrap"
            >
              {editingUser ?'Salvar alterações' : 'Cadastrar usuário'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

